import Vue from 'vue'
import VueRouter, { RouteConfig, Route } from 'vue-router'
import store from '@/store'
import mixins from './mixins'

const GlobalMixins = new mixins()

import { IStatement } from './interfaces'

import Start from '@/views/Start.vue'
import Embed from '@/views/Embed.vue'
import Statement from '@/views/Statement.vue'
import SelectStatements from '@/views/SelectStatements.vue'
import SelectParties from '@/views/SelectParties.vue'
import SelectAnalytics from '@/views/SelectAnalytics.vue'
import Results from '@/views/Results.vue'
import ChoicesOverview from '@/components/ChoicesOverview.vue'

Vue.use(VueRouter)

const routes: RouteConfig[] = [
	{
		path: '/',
		name: 'start',
		component: Start,
		meta: {
			title: GlobalMixins.dictionary.start.tabTitle
		}
	},
	{
		path: `/embed/:id/`,
		name: 'embed',
		component: Embed,
		meta: {
			title: GlobalMixins.dictionary.statement.tabTitle
		}
	},
	{
		path: `/${GlobalMixins.slugify(GlobalMixins.dictionary.statement.tabTitle)}/:id/:title`,
		name: 'statement',
		component: Statement,
		meta: {
			title: GlobalMixins.dictionary.statement.tabTitle
		}
	},
	{
		path: `/${GlobalMixins.slugify(GlobalMixins.dictionary.shootoutStatement.tabTitle)}/:id/:title`,
		name: 'shootout-statement',
		component: Statement,
		meta: {
			title: GlobalMixins.dictionary.shootoutStatement.tabTitle
		}
	},
	{
		path: `/${GlobalMixins.slugify(GlobalMixins.dictionary.selectStatements.tabTitle)}`,
		name: 'select-statements',
		component: SelectStatements,
		meta: {
			title: GlobalMixins.dictionary.selectStatements.tabTitle
		}
	},
	{
		path: `/${GlobalMixins.slugify(GlobalMixins.dictionary.selectParties.tabTitle)}`,
		name: 'select-parties',
		component: SelectParties,
		meta: {
			title: GlobalMixins.dictionary.selectParties.tabTitle
		}
	},
	{
		path: `/${GlobalMixins.slugify(GlobalMixins.dictionary.selectAnalytics.tabTitle)}`,
		name: 'select-analytics',
		component: SelectAnalytics,
		meta: {
			title: GlobalMixins.dictionary.selectAnalytics.tabTitle
		}
	},
	{
		path: `/${GlobalMixins.slugify(GlobalMixins.dictionary.results.tabTitle)}`,
		name: 'results',
		component: Results,
		meta: {
			title: GlobalMixins.dictionary.results.tabTitle
		},
		children: [
			{
				path: `/${GlobalMixins.slugify(GlobalMixins.dictionary.results.tabTitle)}/${GlobalMixins.slugify(GlobalMixins.dictionary.results.choicesOverview.tabTitle)}`,
				name: 'choices-overview',
				component: ChoicesOverview,
				meta: {
					title: GlobalMixins.dictionary.results.choicesOverview.tabTitle
				}
			}
		]
	},
	{
		path: '/pagina-niet-gevonden',
		alias: '*',
		redirect: {
			name: 'start'
		}
	}
]

const router = new VueRouter({
	base: process.env.BASE_URL,
	routes
})

const urlParams = (): any => {
	let params: any = {}
	const parser = document.createElement('a')
	parser.href = window.location.href
	const query = parser.search.substring(1)
	const vars = query.split('&')

	for (let i = 0; i < vars.length; i++) {
		const pair = vars[i].split('=')
		params[pair[0]] = decodeURIComponent(pair[1])
	}

	let allEmpty = true

	Object.keys(params).forEach(key => {
		if (key !== '' && params[key] !== undefined) allEmpty = false
	})

	return allEmpty ? false : params
}

router.beforeEach((to, from, next) => {
	let routeToNavigateTo: any = undefined
	let firstSetup: boolean = store.getters.firstStatementDefaultIndex === false ? true : false
	
	if ('scrollRestoration' in history) {
		history.scrollRestoration = 'manual';
	}

	if (urlParams()) {
		Object.keys(urlParams()).forEach(key => {
			if (key === GlobalMixins.slugify(GlobalMixins.dictionary.statement.tabTitle)) {
				if (/^\d+$/.test(urlParams()[key]) && urlParams()[key].length < 4 && (urlParams()[key].length === 1 || urlParams()[key][0] !== '0')) {
					store.dispatch('setFirstStatement', JSON.parse(urlParams()[key]))
				}
			}
		})
	}

	if ((to.name === 'start' && GlobalMixins.config.settings.skipStart) || (to.name === 'start' && urlParams()[GlobalMixins.slugify(GlobalMixins.dictionary.statement.tabTitle)] && firstSetup)) {
		store.dispatch('skippedStart')

		routeToNavigateTo = {
			name: 'statement',
			params: {
				id: '1',
				title: GlobalMixins.slugify(GlobalMixins.statementTitleToString((store.getters.statement(1) as IStatement).title))
			}
		}
	} else if (to.name === 'statement' || to.name === 'shootout-statement') {
		if (GlobalMixins.config.settings.skipStart && to.name === 'statement') store.dispatch('skippedStart')
		if (to.name === 'statement' && to.params.id === '1' && !from.name) store.dispatch('skippedStart')

		if (to.name === 'shootout-statement' && (store.getters.user.shootoutParties === false || store.getters.user.shootoutParties.length !== 2)) {
			routeToNavigateTo = {
				name: 'results'
			}
		} else if (!to.params.title) {
			routeToNavigateTo = {
				name: to.name,
				params: {
					id: to.params.id,
					title: GlobalMixins.slugify(GlobalMixins.statementTitleToString(store.getters.statement(JSON.parse(to.params.id)).title))
				}
			}
		} else if (to.params.id !== '1' && (JSON.parse(to.params.id) - 1) > (to.name === 'shootout-statement' ? store.getters.user.shootoutChoices.length : store.getters.user.choices.length)) {
			routeToNavigateTo = {
				name: to.name === 'shootout-statement' ? 'results' : 'start'
			}
		}
	} else if (((to.name === 'results' || to.name === 'choices-overview') || to.name === 'select-statements' || to.name === 'select-parties') && (store.getters.user.choices.length !== store.getters.statements.length)) {
		routeToNavigateTo = {
			name: 'start'
		}
	} else if ((to.name === 'select-statements' && GlobalMixins.config.settings.maxSelectedStatements === 0) || (to.name === 'select-analytics' && (!store.getters.user.selectedParties || store.getters.user.selectedParties.length < (GlobalMixins.config.settings.minimumSelectedParties ? GlobalMixins.config.settings.minimumSelectedParties : 3))) || ((to.name === 'results' || to.name === 'choices-overview') && (!store.getters.user.selectedParties || store.getters.user.selectedParties.length < (GlobalMixins.config.settings.minimumSelectedParties ? GlobalMixins.config.settings.minimumSelectedParties : 3)))) {
		routeToNavigateTo = {
			name: 'select-parties'
		}
	} else if (to.name === 'select-analytics' && !store.getters.showAnalyticsQuestion) {
		if (from.name === 'results') {
			routeToNavigateTo = {
				name: 'select-parties'
			}
		} else {
			routeToNavigateTo = {
				name: 'results'
			}
		}
	} else if (to.name === 'choices-overview' && !GlobalMixins.config.settings.showStatementOverview) {
		routeToNavigateTo = {
			name: 'results'
		}
	} else if (to.name === 'embed' && (!to.params.id || Number.isNaN(Number.parseInt(to.params.id)) || !JSON.parse(to.params.id) || GlobalMixins.rawStatements.length < JSON.parse(to.params.id))) {
		routeToNavigateTo = {
			name: 'start'
		}
	} 
	
	if (to.name === 'results' && (from.name === 'select-analytics' || from.name === 'select-parties')) {
		store.dispatch('setShootoutParties', false)
	}

	setTimeout(() => {
		window.scrollTo(0, 0)
	}, 150)

	if (to.name === 'load-user') {
		routeToNavigateTo = false
	}
	
	store.dispatch('setDirection', {
		new: routeToNavigateTo ? routeToNavigateTo : to,
		old: from
	})

	if (routeToNavigateTo) {
		if (routeToNavigateTo.name === 'statement') {
			store.dispatch('startTimer')
		} else {
			store.dispatch('stopTimer')
		}
		
		next(routeToNavigateTo)
	} else {
		if (to.name === 'statement') {
			store.dispatch('startTimer')
		} else {
			store.dispatch('stopTimer')
		}

		next()
	}
})

export default router
